import React, { useState, useMemo, useRef } from 'react';
import tw, { styled } from 'twin.macro';
import PropTypes from 'prop-types';
import { useDrop } from 'react-dnd';
import chroma from 'chroma-js';

import { useMutation } from '@sopranodesign/pulse-hooks';
import { motion, AnimatePresence } from '@sopranodesign/framer-motion';
import Icon from '@sopranodesign/pulse-icons';
import { useTheme } from '@sopranodesign/pulse-volt';

import { useCanvas, useDragPlaceholder, useBuilder } from '../../hooks/index';
import { nodeType } from '../../PropTypes/index';
import nodeTypes from './Types/index';
import blockTypes from '../Blocks/Types/index';
import {
  updateStepPosition as updateStepPositionApi,
  updateStepTransitionPosition as updateStepTransitionPositionApi,
  updateStepTriggerPosition as updateStepTriggerPositionApi
} from '../../api/index';
import { NODE_WIDTH } from '../Canvas/renders';

const Container = styled(motion.div)`
  ${tw`relative`}
`;

const DragPlaceholder = styled(motion.div)`
  ${tw`absolute rounded`}
  border: 2px solid ${chroma('#000').alpha(0.5)};
`;

const Overlay = styled.div`
  ${tw`flex items-center justify-center rounded z-30`}
  color: ${props => (props.borderColour ? props.borderColour : '#007E2E')};
  text-shadow: 0 0 3px #ff0000;
  pointer-events: none;
  background-color: ${props =>
    props.colour
      ? chroma(props.colour).alpha(props.valid ? 0.5 : 0.05)
      : 'none'};
  border: 2px ${props => props.borderColour ?? '#007E2E'};
  border-style: ${props => props.borderStyle ?? 'dashed'};
  width: 100%;
  height: 100%;
  position: absolute;
`;

const categoryNames = {
  step: 'Step Actions',
  transition: 'Transition Actions',
  trigger: 'Transition Actions',
  validation: 'Transition Actions'
};

const useUpdateNodePosition = ({
  node,
  setDragging,
  mouseOffset,
  setMouseOffset,
  selectedNodes
}) => {
  const {
    mousePositionX,
    mousePositionY,
    handleCanvasRender,
    nodes
  } = useCanvas();

  const { conversation, dispatch } = useBuilder();

  const updateStepPosition = useMutation({
    mutationFn: updateStepPositionApi
  });

  const updateStepTransitionPosition = useMutation({
    mutationFn: updateStepTransitionPositionApi
  });

  const updateStepTriggerPosition = useMutation({
    mutationFn: updateStepTriggerPositionApi
  });

  const avoidXZeroBug = x => {
    return x === 0 ? 1 : x;
  };

  const handleUpdateStepPosition = details => {
    details.x = avoidXZeroBug(details.x);
    dispatch({
      type: 'UPDATE_STEP',
      stepId: details.id,
      stepDetails: details
    });
    updateStepPosition.mutate({
      conversationId: conversation.id,
      stepId: details.id,
      position: { x: details.x, y: details.y }
    });
  };

  const handleUpdateTransitionPosition = details => {
    details.x = avoidXZeroBug(details.x);
    dispatch({
      type: 'UPDATE_TRANSITION',
      stepId: details.stepId ? details.stepId : node.meta.stepId,
      transitionId: details.id,
      transitionDetails: details,
      transitionType: details.nodeType
    });

    updateStepTransitionPosition.mutate({
      stepId: details.stepId ? details.stepId : node.meta.stepId,
      transitionId: details.id,
      position: { x: details.x, y: details.y }
    });
  };

  const handleUpdateTriggerPosition = details => {
    details.x = avoidXZeroBug(details.x);
    dispatch({
      type: 'UPDATE_TRIGGER',
      stepId: details.stepId ? details.stepId : node.meta.stepId,
      triggerId: details.id,
      triggerDetails: details
    });
    updateStepTriggerPosition.mutate({
      stepId: details.stepId ? details.stepId : node.meta.stepId,
      triggerId: details.id,
      position: { x: details.x, y: details.y }
    });
  };

  const handleEndDrag = () => {
    setDragging(false);

    const xChange = mousePositionX.get() + mouseOffset.x - node.x;
    const yChange = mousePositionY.get() + mouseOffset.y - node.y;

    const handlers = {
      step: handleUpdateStepPosition,
      transition: handleUpdateTransitionPosition,
      validation: handleUpdateTransitionPosition,
      trigger: handleUpdateTriggerPosition
    };
    const orderedSelectedNodes = selectedNodes.sort((a, b) =>
      a.nodeType > b.nodeType ? 1 : -1
    );
    // checks to see if the dragged node is a part of the selected nodes, if not then it will only move that one node
    const isCurrentNodeSelected = orderedSelectedNodes.find(
      selectedNode =>
        selectedNode.id === node.id && selectedNode.nodeType === node.type
    );

    if (orderedSelectedNodes.length !== 0 && isCurrentNodeSelected) {
      // goes through all nodes that are yet to be handled
      orderedSelectedNodes.forEach(selectedNode => {
        // uses the node id to get all the nodes information
        const selectedNodeInfo = nodes.find(
          element =>
            element.id === selectedNode.id &&
            element.type === selectedNode.nodeType
        );

        // gets the selected nodes handler and handles
        const handler = handlers[selectedNodeInfo.type];

        handler({
          ...selectedNodeInfo.base,
          x: Math.round(selectedNodeInfo.base.x + xChange),
          y: Math.round(selectedNodeInfo.base.y + yChange),
          nodeType: selectedNodeInfo.type,
          stepId: selectedNodeInfo.meta?.stepId
        });
      });
      handleCanvasRender();
    } else {
      const handler = handlers[node.type];
      // handles the move of the dragged node
      handler({
        ...node.base,
        x: Math.round(mousePositionX.get() + mouseOffset.x),
        y: Math.round(mousePositionY.get() + mouseOffset.y),
        nodeType: node.type
      });
    }

    setMouseOffset({
      x: 0,
      y: 0
    });
  };

  return handleEndDrag;
};

const Node = ({ node, x, y, onShiftClick, selectedNodes }) => {
  const ref = useRef(null);
  const [addBlock, setAddBlock] = useState(null);
  const [editBlock, setEditBlock] = useState(null);
  const [hoverStyle, setHoverStyle] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [mouseOffset, setMouseOffset] = useState({ x: 0, y: 0 });
  const theme = useTheme();

  const {
    hoveredBlock,
    panning,
    nodeSizes,
    mousePositionX,
    mousePositionY
  } = useCanvas();

  if (node && node.height && nodeSizes.length) {
    node.height = nodeSizes.filter(
      filteredNode => filteredNode.id === node.id
    )[0].height;
  }

  const { dragging, setDragging, placeholderStyle } = useDragPlaceholder(
    node,
    mouseOffset
  );
  const handleEndDrag = useUpdateNodePosition({
    node,
    setDragging,
    mouseOffset,
    setMouseOffset,
    selectedNodes
  });

  /**
   * https://stackoverflow.com/questions/5429827/how-can-i-prevent-text-element-selection-with-cursor-drag
   */
  const handleStartDrag = event => {
    if (event.shiftKey) {
      onShiftClick(node.type, node.id);
    }

    event.stopPropagation();
    event.preventDefault();
    event.cancelBubble = true;
    event.returnValue = false;

    if (panning || dragging) {
      return false;
    }
    setMouseOffset({
      x: x - mousePositionX.get(),
      y: y - mousePositionY.get()
    });
    setDragging(true);

    return false;
  };

  const { Node: NodeComponent, ...nodeType } = useMemo(() => {
    return nodeTypes[node.style];
  }, [node.style]);

  const { AddBlock } = useMemo(() => {
    if (!addBlock) {
      return {};
    }

    const types = { ...nodeTypes, ...blockTypes };

    return types[addBlock.id];
  }, [addBlock]);

  const { EditBlock } = useMemo(() => {
    if (!editBlock) {
      return {};
    }

    const types = { ...nodeTypes, ...blockTypes };

    return types[editBlock.type];
  }, [editBlock]);

  const [, drop] = useDrop({
    accept: 'BLOCK',
    drop: item => {
      if (nodeType.canDrop(item)) {
        setAddBlock(item);
      }
    },
    canDrop: item => nodeType.canDrop(item),
    collect: monitor => {
      const hovering = monitor.isOver();
      if (!hovering) {
        return setHoverStyle(null);
      }
      setHoverStyle('#007E2E');
    }
  });

  const handleEditBlock = block => {
    setEditBlock(block);
  };

  drop(ref);

  const blockIsValid =
    hoveredBlock && categoryNames[node.type] === hoveredBlock.name;

  const isHoveredSteps = hoveredBlock && hoveredBlock.name !== 'Steps';

  const size = nodeSizes.find(
    info => info.id === node.id && info.type === node.type
  );

  const style = {
    position: 'absolute',
    width: NODE_WIDTH,
    height: size?.height,
    x: x - NODE_WIDTH / 2,
    y: y - size?.height / 2,
    zIndex: isDropdownOpen ? 15 : 10
  };

  return (
    <>
      <AnimatePresence>
        {AddBlock && (
          <AddBlock handleClose={() => setAddBlock(null)} node={node} />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {EditBlock && (
          <EditBlock
            handleClose={() => setEditBlock(null)}
            node={node}
            block={editBlock}
          />
        )}
      </AnimatePresence>
      <Container ref={ref} style={style}>
        {!blockIsValid && hoveredBlock && isHoveredSteps && (
          <Overlay
            colour={theme.danger}
            valid={false}
            borderColour={theme.danger}>
            <Icon solid="true" type="xCircle" />
          </Overlay>
        )}
        {blockIsValid && (
          <Overlay
            transition={{ duration: 0.2 }}
            colour="#F1F8F3"
            borderStyle={hoverStyle ? 'solid' : 'dashed'}
            valid={true}>
            <Icon solid="true" type="plus" />
          </Overlay>
        )}
        {selectedNodes.find(
          selectedNode =>
            selectedNode.id === node.id && selectedNode.nodeType === node.type
        ) && (
            <Overlay
              transition={{ duration: 0.2 }}
              colour="#F1F8F3"
              borderStyle={'solid'}
              valid={true}></Overlay>
          )}
        <div
          role="button"
          tabIndex="0"
          onMouseDown={handleStartDrag}
          onMouseUp={handleEndDrag}
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'between',
            height: '100%'
          }}>
          <NodeComponent
            node={node}
            handleEditBlock={handleEditBlock}
            setIsDropdownOpen={setIsDropdownOpen}
          />
        </div>
      </Container>
      {dragging && (
        <DragPlaceholder style={placeholderStyle} onMouseUp={handleEndDrag} />
      )}
    </>
  );
};

Node.propTypes = {
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  node: nodeType.isRequired,
  onShiftClick: PropTypes.func.isRequired,
  selectedNodes: PropTypes.array.isRequired
};

export { Overlay };
export default Node;
